import { Component, OnInit } from '@angular/core';

import { Modal, ModalService } from '../../../components/modal/modal.service';
import { PromptService } from '../../../components/prompt/prompt.service';
import { SubscriptionsService, TSubscriptionsById } from '../subscriptions.service';

import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TSubscription } from 'src/app/project/data-providers/api-providers/subscription-api-provider/subscription-requests.model';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { generateErrorPrompt } from 'src/app/project/modules/errors/response-error';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventUserSettings,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { ResponseErrorService } from '../../errors/response-error.service';
import { ESubscriptionType } from '../user-settings/subscription-types.enum';
import { TEditNotificationModalData } from './edit-notification-modal.model';
import { EditNotificationModalService } from './edit-notification-modal.service';
import { TSubscriptionTypeOption } from './subscription-type-option.model';
import { SUBSCRIPTION_TYPES } from './subscription-types';

@Component({
  selector: 'app-edit-notifications-modal',
  templateUrl: './edit-notifications-modal.component.html',
  styleUrls: ['./edit-notifications-modal.component.scss'],
  standalone: false,
})
export class EditNotificationsModalComponent implements OnInit {
  subscriptionType: ESubscriptionType;
  subscriptionLabel: string;
  processing = false;
  isBulkEdit = false;

  subscriptionTypes: TSubscriptionTypeOption[] = SUBSCRIPTION_TYPES;

  workspaceId: string;
  userRef: string;
  siteName: string;
  accountName: string;
  newSubscription = false;

  private subscriptionsById: TSubscriptionsById = this.subscriptionsService.getSubscriptionsById();
  private modal: Modal = this.modalService.getModal();

  constructor(
    private subscriptionsService: SubscriptionsService,
    private modalService: ModalService,
    private promptService: PromptService,
    private editNotificationModalService: EditNotificationModalService,
    private translationPipe: TranslationPipe,
    private responseErrorService: ResponseErrorService,
  ) {}

  ngOnInit() {
    if (!this.modal.data) {
      this.isBulkEdit = true;
      this.workspaceId = null;
      this.userRef = null;
      this.siteName = this.translationPipe.transform('edit_all_notifications');
      this.accountName = null;
      this.newSubscription = false;
      this.subscriptionType = null;
    } else {
      this.isBulkEdit = false;
      const modalData: TEditNotificationModalData = this.modal.data;
      this.workspaceId = modalData.workspaceId;
      this.userRef = modalData.userRef;
      this.siteName = modalData.siteName;
      this.accountName = modalData.accountName;

      if (this.subscriptionsById.data[this.workspaceId]) {
        this.subscriptionType = this.subscriptionsById.data[this.workspaceId].subscriptionType;
        this.newSubscription =
          this.subscriptionsById.data[this.workspaceId].subscriptionType === ESubscriptionType.NONE;
      } else {
        this.newSubscription = true;
        this.subscriptionType = ESubscriptionType.NONE;
      }
    }
  }

  hideModal(): void {
    this.modalService.hideModal();
  }

  saveBulk(): void {
    this.isBulkEdit = true;
    this.save();
  }

  save(): void {
    if (!this.subscriptionType || this.processing) {
      return;
    }

    this.processing = true;
    let subscriptionId: string;

    if (this.subscriptionsById.data[this.workspaceId] === undefined) {
      subscriptionId = null;
    } else {
      subscriptionId = this.subscriptionsById.data[this.workspaceId].id;
      this.subscriptionsById.data[this.workspaceId].subscriptionType = this.subscriptionType;
    }

    logEventInGTAG(
      this.isBulkEdit
        ? EGoogleEventUserSettings.PROFILE_SETTINGS__NOTIFICATIONS_BULK
        : EGoogleEventUserSettings.PROFILE_SETTINGS__NOTIFICATIONS,
      {
        source: EGoogleEventCategory.PROFILE_SETTINGS,
        sub_type: this.subscriptionLabel,
      },
    );

    const subscriptionAction$ = this.getSubscriptionAction(subscriptionId);

    if (subscriptionAction$) {
      subscriptionAction$
        .pipe(
          tap((response) => {
            this.success(response);
          }),
          catchError((error) => this.failure(error)),
        )
        .subscribe();
    } else {
      this.success([]);
    }
  }

  private success(subscriptions: TSubscription[]): void {
    this.processing = false;

    this.subscriptionsService.updateSubscriptions(subscriptions);
    this.hideModal();
  }

  private failure(error: string): Observable<never> {
    const promptText = generateErrorPrompt({ message: error }, 'prompt_error');

    this.processing = false;
    this.hideModal();
    this.promptService.showError(promptText);

    return this.responseErrorService.handleRequestError({
      message: 'Failed to update notification settings',
      error,
    });
  }

  toggleSubscriptionType(option: TSubscriptionTypeOption): void {
    this.subscriptionType = option.value;
    this.subscriptionLabel = option.label;
  }

  private getSubscriptionAction(subscriptionId?: string): Observable<TSubscription[]> | null {
    if (this.isBulkEdit) {
      return this.editNotificationModalService.bulkEditNotifications(this.subscriptionType).pipe(
        map((response) => {
          if (this.subscriptionType === ESubscriptionType.NONE) {
            // Type NONE does not exist on BE, it just doesn't return anything
            return Object.values(this.subscriptionsById.data).map((subscription) => ({
              ...subscription,
              subscriptionType: ESubscriptionType.NONE,
            }));
          } else {
            return response;
          }
        }),
      );
    }

    if (this.subscriptionType === ESubscriptionType.NONE && subscriptionId) {
      return this.editNotificationModalService.deleteSubscription(subscriptionId).pipe(
        map((response) => [
          {
            ...this.subscriptionsById.data[subscriptionId],
            subscriptionType: ESubscriptionType.NONE,
          },
        ]),
      );
    }

    if (!this.newSubscription && subscriptionId) {
      return this.editNotificationModalService
        .updateSubscription(subscriptionId, this.subscriptionType)
        .pipe(map((response) => [response]));
    }

    if (this.newSubscription) {
      return this.editNotificationModalService
        .createSubscription(this.subscriptionType, this.workspaceId, this.userRef)
        .pipe(map((response) => [response]));
    }

    return null;
  }
}
